import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
  defaultAnimateLayoutChanges,
  type AnimateLayoutChanges,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { X } from "lucide-react";

type Img = { id: string; orden: number; thumbUrl: string };

// Forzamos animar el cambio de layout del item recién arrastrado → al soltar
// DESLIZA a su nueva posición en vez de pegar el saltito (bug al mover der→izq).
const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

// Una miniatura arrastrable (toda la miniatura es el handle). Hace un "wiggle"
// sutil para señalar que se puede mover; la × (quitar) va adentro arriba a la
// derecha. El orden es izquierda→derecha (sin números, se entiende solo).
function SortableThumb({
  img,
  index,
  onRemove,
}: {
  img: Img;
  index: number;
  onRemove: (id: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: img.id, animateLayoutChanges });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="relative shrink-0 cursor-grab active:cursor-grabbing touch-none"
    >
      <div
        className="relative w-16 h-16 rounded-md overflow-hidden shadow-sm"
        style={{
          // el que se arrastra no hace wiggle (queda derecho mientras lo movés)
          animation: isDragging ? "none" : "tn-wiggle 0.55s ease-in-out infinite",
          animationDelay: `-${(index * 0.13).toFixed(2)}s`,
        }}
      >
        <img
          src={img.thumbUrl}
          alt=""
          className="w-full h-full object-cover pointer-events-none"
        />
      </div>
      <span className="absolute top-0.5 left-0.5 z-10 w-4 h-4 rounded-full bg-brand-primary text-white inline-flex items-center justify-center text-[10px] font-semibold">
        {index + 1}
      </span>
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => onRemove(img.id)}
        className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/55 text-white inline-flex items-center justify-center hover:bg-black/75 z-10"
      >
        <X className="w-2.5 h-2.5" />
      </button>
    </div>
  );
}

// Grilla de miniaturas arrastrables (drag-and-drop) para reordenar las fotos.
// El orden vive afuera; emitimos el nuevo orden de ids al soltar.
export function SortableImageList({
  imagenes,
  onReorder,
  onRemove,
}: {
  imagenes: Img[];
  onReorder: (idsEnOrden: string[]) => void;
  onRemove: (id: string) => void;
}) {
  // distance:5 → distingue tap de drag (anda en mouse y touch).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );
  const ids = imagenes.map((i) => i.id);
  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (over && active.id !== over.id) {
      const oldIndex = ids.indexOf(String(active.id));
      const newIndex = ids.indexOf(String(over.id));
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(arrayMove(ids, oldIndex, newIndex));
      }
    }
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <style>
        {"@keyframes tn-wiggle{0%,100%{transform:rotate(-1.6deg)}50%{transform:rotate(1.6deg)}}"}
      </style>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div className="flex flex-wrap gap-3 py-1">
          {imagenes.map((img, i) => (
            <SortableThumb key={img.id} img={img} index={i} onRemove={onRemove} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export default SortableImageList;
